/**
 * TaskAccordion — la tarea como acordeón DENTRO de la lista. Decisión de
 * producto: el detalle de tarea no existe como pantalla — una tarea es un
 * registro chico sin fases; abrir, decidir y seguir sin perder el lugar.
 *
 * La fila colapsada es LogEntry (el MISMO componente de la bitácora):
 * disco por tipo (--evo-task-*), relleno tenue = espera algo de ti, solo
 * contorno = corre sola. El borde de la tarjeta acentúa: ámbar = requiere
 * aprobación, rojo = falló definitivamente.
 *
 * Todo lo que vivía en TaskDetailPage está acá: descripción, propuesta de
 * EvoBrain (gated, con aprobar/rechazar — primero cuando es lo único que
 * viniste a hacer), programación/origen, historial, log del último intento
 * con el ERROR REAL, archivos, reportes y las acciones que le tocan a ESA
 * tarea. EditTaskModal se conserva tal cual: diez campos no caben acá.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { Icon, type IconName } from '../Icon.js';
import { Checkbox } from '../ui/Checkbox.js';
import { KVRow } from '../ui/KVRow.js';
import { Modal } from '../ui/Modal.js';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { Select } from '../ui/Select.js';
import { DatePicker } from '../ui/DatePicker.js';
import { Field } from '../ui/Field.js';
import { Pill, type PillTone } from '../PillGroup.js';
import { SubRow, SubRowList } from '../ui/SubRow.js';
import { ApprovalCard, type ApprovalProposalItem, type ProposalVerb } from '../ui/ApprovalCard.js';
import {
  useApproveTask, useRetryTask, useRunTaskNow, useDeleteTask,
  usePatchTask, useTaskWorkspaceFiles,
  useApproveProposalTask, useRejectProposalTask,
  type Task,
} from '../../hooks/useTasks.js';
import { useProject } from '../../hooks/useProjects.js';
import { useContact } from '../../hooks/useContacts.js';
import { useReportsForTask } from '../../hooks/useReports.js';
import { parseServerDate } from '../../lib/date.js';

const STATUS_TONES: Record<string, PillTone> = {
  pending: 'neutral', needs_approval: 'amber', approved: 'cyan', in_progress: 'blue',
  done: 'success', failed: 'danger', blocked: 'muted',
};
const PRIORITY_TONES: Record<string, PillTone> = { alta: 'danger', media: 'amber', baja: 'muted' };

// Tipo → glifo + token de color. El relleno del disco NO depende del tipo:
// dice si la tarea espera algo de ti (misma convención que in/out en bitácora).
const TYPE_GLYPH: Record<string, { icon: IconName; color: string }> = {
  research: { icon: 'leads', color: 'var(--evo-task-research)' },
  write: { icon: 'compose', color: 'var(--evo-task-draft)' },
  'disk-check': { icon: 'activity', color: 'var(--evo-task-system)' },
  sysadmin: { icon: 'activity', color: 'var(--evo-task-system)' },
  general: { icon: 'send', color: 'var(--evo-task-send)' },
};

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return parseServerDate(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** ¿Espera una decisión del usuario? → banda 1, relleno del disco, borde ámbar. */
export function needsAttention(task: Task): boolean {
  if (task.status === 'needs_approval') return true;
  if (task.status === 'pending' && task.executor === 'agent-cli') return true;
  if (task.executor === 'tool' && task.proposedActionJson && task.status !== 'done' && task.status !== 'failed') return true;
  return false;
}

/** Falló sin más reintentos programados. */
function failedForGood(task: Task): boolean {
  return task.status === 'failed' || task.status === 'blocked';
}

// ── Entidad ligada (misma resolución que tenía TaskDetailPage) ───────────────

function useLinkedEntity(task: Task): { label: string; href: string } | null {
  const projectId = task.entityType === 'project' && task.entityId ? parseInt(task.entityId, 10) : undefined;
  const contactId = (task.entityType === 'contact' && task.entityId ? parseInt(task.entityId, 10) : null) ?? task.contactId;
  const { data: project } = useProject(task.entityType === 'project' ? projectId : undefined);
  const { data: contact } = useContact(contactId);

  if (task.entityType === 'project' && project) return { label: project.name, href: `/proyectos/${project.slug}` };
  if (contact) {
    const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.businessName || `#${contact.id}`;
    return { label: name, href: `/contacts/${contact.id}` };
  }
  if (task.entityType === 'lead' && task.entityId) return { label: `Lead #${task.entityId}`, href: `/leads/${task.entityId}` };
  return null;
}

// ── Edit modal (se conserva tal cual del detalle viejo) ──────────────────────

const TYPE_VALUES = ['general', 'research', 'write', 'disk-check', 'sysadmin'] as const;
const PRIORITY_VALUES = ['alta', 'media', 'baja'] as const;
const OUTPUT_MODE_VALUES = ['per_run', 'single_doc'] as const;

interface EditForm {
  title: string; description: string; type: string; priority: string; category: string;
  outputMode: string; nextRunAt: Date | null; repeatMinutes: string; repeatUntil: Date | null; maxRuns: string;
}

function taskToForm(task: Task): EditForm {
  return {
    title: task.title, description: task.description, type: task.type, priority: task.priority,
    category: task.category, outputMode: task.outputMode,
    nextRunAt: task.nextRunAt ? parseServerDate(task.nextRunAt) : null,
    repeatMinutes: task.repeatMinutes != null ? String(task.repeatMinutes) : '',
    repeatUntil: task.repeatUntil ? parseServerDate(task.repeatUntil) : null,
    maxRuns: task.maxRuns != null ? String(task.maxRuns) : '',
  };
}

export function EditTaskModal({ task, open, onClose }: { task: Task; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const patch = usePatchTask();
  const [form, setForm] = useState<EditForm>(() => taskToForm(task));
  useEffect(() => { if (open) setForm(taskToForm(task)); }, [open, task]);

  function set<K extends keyof EditForm>(key: K, value: EditForm[K]) {
    setForm(f => ({ ...f, [key]: value }));
  }

  function handleSave() {
    const repeatMinutes = form.repeatMinutes.trim() ? parseInt(form.repeatMinutes, 10) : null;
    const maxRuns = form.maxRuns.trim() ? parseInt(form.maxRuns, 10) : null;
    patch.mutate({
      id: task.id, title: form.title.trim(), description: form.description, type: form.type,
      priority: form.priority, category: form.category, outputMode: form.outputMode,
      nextRunAt: form.nextRunAt ? form.nextRunAt.toISOString() : null,
      repeatMinutes: Number.isFinite(repeatMinutes) ? repeatMinutes : null,
      repeatUntil: form.repeatUntil ? form.repeatUntil.toISOString() : null,
      maxRuns: Number.isFinite(maxRuns) ? maxRuns : null,
    }, { onSuccess: onClose });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('tasks.edit.title')} size="lg">
      <div className="flex flex-col" style={{ gap: '4px' }}>
        <Field label={t('tasks.fields.title')}><Input value={form.title} onChange={v => set('title', v)} fullWidth /></Field>
        <Field label={t('tasks.fields.description')}><Textarea value={form.description} onChange={v => set('description', v)} rows={8} fullWidth /></Field>
        <Field label={t('tasks.fields.type')}>
          <Select value={form.type} onChange={v => set('type', v ?? form.type)} options={TYPE_VALUES.map(v => ({ value: v, label: t(`tasks.type.${v}`, { defaultValue: v }) }))} fullWidth />
        </Field>
        <Field label={t('tasks.fields.priority')}>
          <Select value={form.priority} onChange={v => set('priority', v ?? form.priority)} options={PRIORITY_VALUES.map(v => ({ value: v, label: t(`tasks.priority.${v}`, { defaultValue: v }) }))} fullWidth />
        </Field>
        <Field label={t('tasks.fields.category')}><Input value={form.category} onChange={v => set('category', v)} fullWidth /></Field>
        <Field label={t('tasks.fields.outputMode')}>
          <Select value={form.outputMode} onChange={v => set('outputMode', v ?? form.outputMode)} options={OUTPUT_MODE_VALUES.map(v => ({ value: v, label: t(`tasks.outputMode.${v}`, { defaultValue: v }) }))} fullWidth />
        </Field>
        <Field label={t('tasks.fields.nextRunAt')}><DatePicker mode="single" value={form.nextRunAt} onChange={v => set('nextRunAt', v)} fullWidth /></Field>
        <Field label={t('tasks.fields.repeatMinutes')}><Input type="number" value={form.repeatMinutes} onChange={v => set('repeatMinutes', v)} fullWidth /></Field>
        <Field label={t('tasks.fields.repeatUntil')}><DatePicker mode="single" value={form.repeatUntil} onChange={v => set('repeatUntil', v)} fullWidth /></Field>
        <Field label={t('tasks.fields.maxRuns')}><Input type="number" value={form.maxRuns} onChange={v => set('maxRuns', v)} fullWidth /></Field>
      </div>
      <div className="flex justify-end" style={{ gap: 'var(--evo-space-2)', marginTop: 'var(--evo-space-4)' }}>
        <Button variant="outline" tone="muted" size="sm" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="solid" tone="cyan" size="sm" loading={patch.isPending} onClick={handleSave}>{t('common.save')}</Button>
      </div>
    </Modal>
  );
}

// ── Bloques del cuerpo ────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: string }) {
  return (
    <span style={{ display: 'block', fontSize: 'var(--evo-fs-caption)', fontWeight: 'var(--evo-fw-medium)', letterSpacing: 'var(--evo-ls-caps)', textTransform: 'uppercase', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', marginBottom: 'var(--evo-space-2)' }}>
      {children}
    </span>
  );
}

function WorkspaceFiles({ taskId }: { taskId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useTaskWorkspaceFiles(taskId);
  const files = data?.files ?? [];
  if (isLoading || files.length === 0) return null;
  return (
    <div>
      <SectionLabel>{t('tasks.detail.files')}</SectionLabel>
      <SubRowList>
        {files.map(f => (
          <SubRow key={f.name} title={f.name} meta={`${fmtBytes(f.sizeBytes)} · ${fmt(f.modifiedAt)}`} />
        ))}
      </SubRowList>
    </div>
  );
}

function TaskReports({ taskId }: { taskId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useReportsForTask(taskId);
  const reports = data?.reports ?? [];
  if (reports.length === 0) return null;
  return (
    <div>
      <SectionLabel>{t('tasks.detail.reports')}</SectionLabel>
      <SubRowList>
        {reports.map(r => (
          <SubRow key={r.id} title={r.title} onClick={() => navigate(`/reports/${r.slug}`)} />
        ))}
      </SubRowList>
    </div>
  );
}

// ── El acordeón ───────────────────────────────────────────────────────────────

interface TaskAccordionProps {
  task: Task;
  open: boolean;
  onToggle: () => void;
  checked: boolean;
  onCheck: () => void;
}

export function TaskAccordion({ task, open, onToggle, checked, onCheck }: TaskAccordionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const approve = useApproveTask();
  const approveProposal = useApproveProposalTask();
  const rejectProposal = useRejectProposalTask();
  const retry = useRetryTask();
  const runNow = useRunTaskNow();
  const del = useDeleteTask();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const linked = useLinkedEntity(task);

  const glyph = TYPE_GLYPH[task.type] ?? TYPE_GLYPH['general']!;
  const attention = needsAttention(task);
  const borderColor = attention ? 'var(--evo-amber)' : failedForGood(task) ? 'var(--evo-danger)' : 'var(--evo-stroke)';

  const resultText = task.result
    ? task.result.replace(/\s+/g, ' ').slice(0, 60)
    : task.lastError ? task.lastError.replace(/\s+/g, ' ').slice(0, 60) : t('tasks.row.noResult');

  // Propuesta gated de EvoBrain — primero: es lo único que viniste a hacer.
  let proposalBlock: React.ReactNode = null;
  if (task.executor === 'tool' && task.proposedActionJson) {
    type ProposedAction = { toolName: string; turnGroupId: string; verb: ProposalVerb; summary: { sentence: string; changes?: Array<{ field: string; from: string | null; to: string }>; irreversible?: boolean; warning?: string } };
    let parsed: ProposedAction | null = null;
    try { parsed = JSON.parse(task.proposedActionJson) as ProposedAction; } catch { /* payload corrupto — se omite */ }
    if (parsed) {
      const item: ApprovalProposalItem = {
        id: task.proposalMessageId ?? 0,
        taskId: task.id,
        turnGroupId: parsed.turnGroupId,
        toolName: parsed.toolName,
        verb: parsed.verb,
        sentence: parsed.summary.sentence,
        changes: parsed.summary.changes,
        irreversible: parsed.summary.irreversible,
        warning: parsed.summary.warning,
        createdAt: task.createdAt,
        status: task.status === 'done' ? 'approved' : task.status === 'failed' ? 'rejected' : 'pending',
      };
      proposalBlock = (
        <ApprovalCard
          proposals={[item]}
          entity={linked}
          onApprove={(pid, remember) => approveProposal.mutate({ id: pid, remember: Boolean(remember) })}
          onReject={pid => rejectProposal.mutate(pid)}
          approvingId={approveProposal.isPending ? task.id : null}
          rejectingId={rejectProposal.isPending ? task.id : null}
        />
      );
    }
  }

  return (
    <div
      data-task-id={task.id}
      style={{ border: `1px solid ${borderColor}`, borderRadius: 'var(--evo-r)', overflow: 'hidden' }}
    >
      {/* Fila colapsada — misma anatomía que la entrada de bitácora, con el
          checkbox integrado (REGLA #18). Fondo permanente del diseño
          (--evo-log-row-bg); el hover solo lo densifica. */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--evo-log-icon-gap)',
          minHeight: 'var(--evo-log-row-min-h)', padding: 'var(--evo-log-row-py) var(--evo-log-row-px)',
          background: 'var(--evo-log-row-bg)',
          cursor: 'pointer', userSelect: 'none', boxSizing: 'border-box',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-log-row-bg-hover)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-log-row-bg)'; }}
      >
        <span style={{ display: 'flex', flexShrink: 0 }} onClick={e => { e.stopPropagation(); onCheck(); }}>
          <span style={{ pointerEvents: 'none', display: 'flex' }}><Checkbox checked={checked} onChange={() => {}} /></span>
        </span>

        {/* Disco del tipo: color por tipo, RELLENO tenue = espera algo de ti,
            solo contorno = corre sola (misma convención in/out de bitácora). */}
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 'var(--evo-log-icon-sz)', height: 'var(--evo-log-icon-sz)',
          borderRadius: 'var(--evo-r-pill)', flexShrink: 0, boxSizing: 'border-box',
          border: `1px solid ${glyph.color}`,
          background: attention ? `color-mix(in srgb, ${glyph.color} 14%, transparent)` : 'transparent',
          color: glyph.color,
        }}>
          <Icon name={glyph.icon} size={13} />
        </span>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <span className="truncate" style={{ fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-1)' }}>
            {task.plainSummary ?? task.title}
          </span>
          {linked && (
            <span className="truncate" style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)' }}>
              {t('tasks.row.linked', { name: linked.label })}
            </span>
          )}
        </div>

        <Pill tone={PRIORITY_TONES[task.priority] ?? 'muted'} variant="outline" size="xs">{t(`tasks.priority.${task.priority}`, { defaultValue: task.priority })}</Pill>
        <Pill tone={STATUS_TONES[task.status] ?? 'neutral'} variant="outline" size="xs">{t(`tasks.status.${task.status}`)}</Pill>
        <span style={{ flexShrink: 0, width: 'var(--evo-task-result-w)', textAlign: 'right', fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: task.lastError && !task.result ? 'var(--evo-danger)' : 'var(--evo-fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {resultText}
        </span>
        <span style={{ flexShrink: 0, width: 'var(--evo-task-meta-w)', textAlign: 'right', fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {fmt(task.nextRunAt)}
        </span>
        <span style={{ flexShrink: 0, display: 'inline-flex', color: 'var(--evo-fg-4)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <Icon name="chevron-down" size={12} />
        </span>
      </div>

      {/* Cuerpo expandido — dentro de la MISMA tarjeta, separado por un hilo. */}
      {open && (
        <div style={{ padding: '0 var(--evo-log-row-px) var(--evo-log-body-py)', boxSizing: 'border-box' }}>
          <div style={{ padding: 'var(--evo-space-4) 0 0', borderTop: '1px solid var(--evo-stroke)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-5)' }}>
            {/* 1 · qué hace, en una frase */}
            {task.description && (
              <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', lineHeight: 1.7, color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', whiteSpace: 'pre-wrap' }}>
                {task.description}
              </p>
            )}

            {/* propuesta gated — antes que todo lo demás */}
            {proposalBlock}

            {/* 2 · programación | origen */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(var(--evo-related-col-min), 1fr))', gap: 'var(--evo-space-4) var(--evo-space-6)', alignItems: 'start' }}>
              <div>
                <SectionLabel>{t('tasks.sections.schedule')}</SectionLabel>
                <KVRow label={t('tasks.origin2.every')} value={task.repeatMinutes ? t('tasks.origin2.everyValue', { min: task.repeatMinutes }) : '—'} />
                <KVRow label={t('tasks.fields.nextRunAt')} value={fmt(task.nextRunAt)} />
                <KVRow label={t('tasks.fields.repeatUntil')} value={fmt(task.repeatUntil)} />
                <KVRow label={t('tasks.fields.maxRuns')} value={task.maxRuns != null ? String(task.maxRuns) : '—'} />
                <KVRow label={t('tasks.fields.attempts')} value={String(task.attempts)} />
                <KVRow label={t('tasks.fields.runs')} value={String(task.runs)} last />
              </div>
              <div>
                <SectionLabel>{t('tasks.sections.origin')}</SectionLabel>
                <KVRow
                  label={t('tasks.row.linked', { name: '' }).replace(/\s+$/, '')}
                  value={linked ? (
                    <Button variant="link" tone="cyan" size="sm" onClick={() => navigate(linked.href)}>{linked.label}</Button>
                  ) : '—'}
                />
                <KVRow label={t('tasks.fields.type')} value={t(`tasks.type.${task.type}`, { defaultValue: task.type })} />
                <KVRow label={t('tasks.fields.category')} value={task.category || '—'} />
                <KVRow label={t('tasks.fields.outputMode')} value={t(`tasks.outputMode.${task.outputMode}`, { defaultValue: task.outputMode })} />
                <KVRow label={t('tasks.origin2.createdBy')} value={task.source === 'evo-brain' ? t('tasks.origin2.sourceEvoBrain') : t('tasks.origin2.sourceUser')} />
                <KVRow label={t('tasks.origin2.created')} value={fmt(task.createdAt)} last />
              </div>
            </div>

            {/* 3 · historial — hoy el backend guarda SOLO la corrida más
                reciente (result/lastError); el historial por corrida es
                pendiente de backend y acá no se inventa. */}
            {(task.result || task.lastError) && (
              <div>
                <SectionLabel>{t('tasks.sections.history')}</SectionLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)', padding: 'var(--evo-task-log-py) 0' }}>
                  <span style={{ width: '110px', flexShrink: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
                    {fmt(task.updatedAt)}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t('tasks.historyRow')}
                  </span>
                  {task.result && !task.lastError
                    ? <Pill variant="outline" tone="cyan" size="xs">{t('tasks.status.done')}</Pill>
                    : task.lastError
                      ? <Pill variant="outline" tone="danger" size="xs">{t('tasks.status.failed')}</Pill>
                      : null}
                </div>
              </div>
            )}

            {/* 4 · log del último intento — con el error REAL */}
            {(task.lastError || task.result) && (
              <div>
                <SectionLabel>{t('tasks.sections.lastAttempt')}</SectionLabel>
                <pre style={{ margin: 0, padding: 'var(--evo-space-3) var(--evo-space-4)', background: 'var(--evo-bg-3)', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-sm)', fontFamily: 'var(--evo-font-mono)', fontSize: 'var(--evo-fs-caption)', lineHeight: 1.6, color: task.lastError ? 'var(--evo-danger)' : 'var(--evo-fg-3)', whiteSpace: 'pre-wrap', overflowX: 'auto', wordBreak: 'break-word' }}>
                  {task.lastError ?? task.result}
                </pre>
              </div>
            )}

            {/* 5 · archivos y reportes (solo agent-cli con corridas) */}
            {task.executor === 'agent-cli' && task.runs > 0 && <WorkspaceFiles taskId={task.id} />}
            {task.executor === 'agent-cli' && <TaskReports taskId={task.id} />}

            {/* 6 · acciones — las que le tocan a ESTA tarea; una sola sólida */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
              {!confirmDelete ? (
                <>
                  {(task.status === 'needs_approval' || (task.status === 'pending' && task.executor === 'agent-cli')) && task.executor !== 'tool' && (
                    <Button size="sm" variant="solid" tone="cyan" icon={<Icon name="check" size={12} />} loading={approve.isPending} onClick={() => approve.mutate(task.id)}>
                      {t('tasks.actions.approve')}
                    </Button>
                  )}
                  {(task.status === 'approved' || task.status === 'done') && task.executor !== 'tool' && (
                    <Button size="sm" variant="outline" tone="cyan" icon={<Icon name="bolt" size={12} />} loading={runNow.isPending} onClick={() => runNow.mutate(task.id)}>
                      {t('tasks.actions.runNow')}
                    </Button>
                  )}
                  {failedForGood(task) && (
                    <Button size="sm" variant="outline" tone="cyan" icon={<Icon name="refresh" size={12} />} loading={retry.isPending} onClick={() => retry.mutate(task.id)}>
                      {t('tasks.actions.retry')}
                    </Button>
                  )}
                  {task.status !== 'in_progress' && (
                    <Button size="sm" variant="outline" tone="muted" icon={<Icon name="compose" size={12} />} onClick={() => setEditOpen(true)}>
                      {t('tasks.actions.edit')}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" tone="danger" icon={<Icon name="trash" size={12} />} onClick={() => setConfirmDelete(true)}>
                    {t('tasks.actions.delete')}
                  </Button>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-danger)', fontFamily: 'var(--evo-font)', whiteSpace: 'nowrap' }}>{t('tasks.deleteConfirm')}</span>
                  <Button size="sm" variant="solid" tone="danger" loading={del.isPending} onClick={() => del.mutate(task.id)}>{t('tasks.actions.delete')}</Button>
                  <Button size="sm" variant="outline" tone="muted" onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {task.status !== 'in_progress' && (
        <EditTaskModal task={task} open={editOpen} onClose={() => setEditOpen(false)} />
      )}
    </div>
  );
}
